<script setup lang="ts">
import { computed, ref, type HTMLAttributes, type Component } from 'vue'
import { SwitchRoot, type SwitchRootProps, SwitchThumb } from 'reka-ui'
import { cn } from '@/lib/utils'

type SwitchSize = 'default' | 'sm'

const props = withDefaults(
  defineProps<
    Omit<SwitchRootProps, 'modelValue' | 'defaultValue' | 'trueValue' | 'falseValue'> & {
      class?: HTMLAttributes['class']
      modelValue?: boolean | null
      defaultValue?: boolean
      checked?: boolean
      defaultChecked?: boolean
      label?: string
      description?: string
      leftIcon?: Component
      containerClass?: HTMLAttributes['class']
      size?: SwitchSize
    }
  >(),
  { size: 'default' }
)
const emits = defineEmits<{
  'update:modelValue': [payload: boolean]
  'update:checked': [payload: boolean]
}>()

const localChecked = ref(props.defaultValue ?? props.defaultChecked ?? false)

const internalChecked = computed({
  get: () => {
    return props.modelValue ?? props.checked ?? localChecked.value
  },
  set: (val) => {
    if (props.modelValue === undefined && props.checked === undefined) {
      localChecked.value = val
    }
    emits('update:modelValue', val)
    emits('update:checked', val)
  }
})

const rootSizeClass = computed(() => (props.size === 'sm' ? 'h-4 w-7' : 'h-6 w-11'))

const thumbSizeClass = computed(() =>
  props.size === 'sm'
    ? 'h-3 w-3 data-[state=checked]:translate-x-3'
    : 'h-5 w-5 data-[state=checked]:translate-x-5'
)

const delegatedProps = computed(() => {
  const delegated = { ...props }
  delete (delegated as Record<string, unknown>).class
  delete (delegated as Record<string, unknown>).modelValue
  delete (delegated as Record<string, unknown>).defaultValue
  delete (delegated as Record<string, unknown>).checked
  delete (delegated as Record<string, unknown>).defaultChecked
  delete (delegated as Record<string, unknown>).label
  delete (delegated as Record<string, unknown>).description
  delete (delegated as Record<string, unknown>).leftIcon
  delete (delegated as Record<string, unknown>).containerClass
  delete (delegated as Record<string, unknown>).size
  return delegated
})

const toggle = () => {
  if (props.disabled) return
  internalChecked.value = !internalChecked.value
}
</script>

<template>
  <template v-if="props.label">
    <div
      :class="
        cn(
          'flex items-center justify-between gap-4 rounded-xl border p-4 select-none transition-colors bg-card',
          props.disabled ? 'opacity-60 pointer-events-none' : 'cursor-pointer hover:bg-muted/50',
          props.containerClass
        )
      "
      @click="toggle"
    >
      <div class="flex items-center gap-4">
        <div v-if="props.leftIcon" class="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
          <component :is="props.leftIcon" class="h-5 w-5" />
        </div>
        <div class="space-y-1">
          <p class="text-sm font-medium text-foreground">{{ props.label }}</p>
          <p v-if="props.description" class="text-xs text-muted-foreground">
            {{ props.description }}
          </p>
        </div>
      </div>
      <SwitchRoot
        v-bind="delegatedProps"
        v-model="internalChecked"
        :class="
          cn(
            'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
            rootSizeClass,
            props.class
          )
        "
        @click.stop
      >
        <SwitchThumb
          :class="
            cn(
              'pointer-events-none block rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=unchecked]:translate-x-0',
              thumbSizeClass
            )
          "
        />
      </SwitchRoot>
    </div>
  </template>
  <template v-else>
    <SwitchRoot
      v-bind="delegatedProps"
      v-model="internalChecked"
      :class="
        cn(
          'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
          rootSizeClass,
          props.class
        )
      "
    >
      <SwitchThumb
        :class="
          cn(
            'pointer-events-none block rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=unchecked]:translate-x-0',
            thumbSizeClass
          )
        "
      />
    </SwitchRoot>
  </template>
</template>
